import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface NewsCardProps {
  index: number
  title: string
  excerpt: string
  date: string
  category: string
  image: string
  onOpen: () => void
  className?: string
}

export function NewsCard({
  index,
  title,
  excerpt,
  date,
  category,
  image,
  onOpen,
  className,
}: NewsCardProps) {
  const { t, i18n } = useTranslation(['blog', 'common'])
  const displayIndex = String(index + 1).padStart(2, '0')

  const formattedDate = new Date(date).toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <article
      className={cn(
        'group relative flex flex-col h-full',
        'bg-ink rounded-xl overflow-hidden',
        'border border-white/10',
        'hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5',
        'transition-all duration-300',
        className
      )}
    >
      {/* Editorial index number */}
      <span
        className="absolute top-4 left-4 z-10 font-display-italic text-4xl text-primary/20 select-none"
        aria-hidden="true"
      >
        {displayIndex}
      </span>

      {/* Image */}
      <div className="aspect-[16/10] overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-primary-soft flex items-center justify-center">
            <span className="font-display text-4xl text-primary/40">AD</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col flex-grow p-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="px-2.5 py-1 text-xs font-medium tracking-wide uppercase rounded-full bg-primary/10 text-primary border border-primary/20">
            {category}
          </span>
          <time
            dateTime={date}
            className="text-xs text-white/50"
          >
            {formattedDate}
          </time>
        </div>

        <h3 className="font-display text-xl text-white group-hover:text-primary transition-colors duration-300 line-clamp-2">
          {title}
        </h3>

        {excerpt && (
          <p className="mt-2 text-sm text-white/60 leading-relaxed line-clamp-3 flex-grow">
            {excerpt}
          </p>
        )}

        <button
          type="button"
          onClick={onOpen}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-light transition-colors w-fit"
        >
          {t('blog:modal.readArticle', { defaultValue: t('common:buttons.readMore') })}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </article>
  )
}
